import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Table, Tag, Dropdown, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  IconArrowRight,
  IconSearch,
  IconStarFilled,
} from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import ConfirmModal from "../../components/modal/ConfirmModal";
import LoanFormDrawer from "./LoanFormDrawer";
import api from "../../api";
import { formatDateTime } from "../../utils/helper";
import {
  LOAN_CATEGORY_OPTIONS,
  LOAN_TYPE_COLOR,
  LOAN_TYPE_LABEL,
  LOAN_TYPE_OPTIONS,
} from "../../constants";

export default function Loan() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [q, setQ] = useState("");
  const [type, setType] = useState(undefined);
  const [categoryId, setCategoryId] = useState(undefined);
  const [page, setPage] = useState(1);

  const toast = useToast();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const res = await api.loans_list({
      page,
      limit: 10,
      ...(q && { q }),
      ...(type && { type }),
      ...(categoryId && { categoryId }),
    });
    if (res?.status === 200) {
      setRows(res.data.data);
      setMeta(res.data.meta);
    } else {
      toast("Өгөгдөл авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, [page, q, type, categoryId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const onTypeChange = (v) => {
    setType(v);
    setCategoryId(undefined);
    setPage(1);
    setRows([]);
  };

  const onCategoryChange = (v) => {
    setCategoryId(v);
    setPage(1);
    setRows([]);
  };

  const openCreate = () => {
    setEditingRow(null);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditingRow(record);
    setDrawerOpen(true);
  };

  const onDrawerSuccess = (data, isEdit) => {
    if (isEdit) {
      toast("Амжилттай засагдлаа", "success");
    } else {
      toast("Амжилттай нэмэгдлээ", "success");
    }
    fetchRows();
  };

  const onDelete = async () => {
    setDeleting(true);
    const res = await api.loans_delete(deleteModal.id);
    if (res?.status === 200) {
      toast("Устгагдлаа", "info");
      setRows((prev) => prev.filter((r) => r.id !== deleteModal.id));
    } else {
      toast(res?.data?.message || "Устгахад алдаа гарлаа", "error");
    }
    setDeleting(false);
    setDeleteModal({ open: false, id: null });
  };

  const columns = [
    {
      title: "Онцлох",
      dataIndex: "featured",
      width: 70,
      align: "center",
      render: (v) =>
        v ? (
          <IconStarFilled size={16} style={{ color: "#faad14" }} />
        ) : (
          <span style={{ color: "#ddd" }}>-</span>
        ),
    },
    {
      title: "Зураг",
      dataIndex: "imageUrl",
      width: 60,
      render: (v) =>
        v ? (
          <img
            src={v}
            alt="loan"
            style={{ width: 40, height: 25, objectFit: "contain" }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: 12 }}>—</span>
        ),
    },
    {
      title: "Нэр",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div className="fw-semibold">{v}</div>
          {r.highlight && (
            <div style={{ fontSize: 11, color: "#888" }}>{r.highlight}</div>
          )}
        </div>
      ),
    },
    {
      title: "Байгууллага",
      dataIndex: "issuer",
      render: (v) => v?.name || "—",
    },
    {
      title: "Төрөл",
      dataIndex: "type",
      render: (v) =>
        v ? (
          <Tag color={LOAN_TYPE_COLOR(v)}>{LOAN_TYPE_LABEL(v)}</Tag>
        ) : (
          <span style={{ color: "#ccc" }}>-</span>
        ),
    },
    {
      title: "Ангилал",
      dataIndex: "categories",
      render: (cats) =>
        cats?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {cats.map((c) => (
              <Tag key={c.id}>{c.label}</Tag>
            ))}
          </div>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        ),
    },
    {
      title: "Сарын хүү",
      dataIndex: "minMonthlyInterest",
      render: (v, r) => {
        if (v == null && r.maxMonthlyInterest == null) return "—";
        if (v != null && r.maxMonthlyInterest != null)
          return `${v}% – ${r.maxMonthlyInterest}%`;
        return `${v ?? r.maxMonthlyInterest}%`;
      },
    },
    {
      title: "Дээд хэмжээ",
      dataIndex: "maxAmount",
      render: (v) => (v != null ? `₮${v.toLocaleString()}` : "—"),
    },
    {
      title: "Үнэлгээ",
      dataIndex: "rating",
      render: (v) => (v != null ? `⭐ ${v}` : "—"),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      minWidth: 150,
      render: (v) => <div>{formatDateTime(v)}</div>,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete")
                setDeleteModal({ open: true, id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="loan-section">
      <PageHeader
        title="Зээл"
        sub="Банк, ББСБ-ын зээлийн төрлүүд, хүү ба нөхцөлүүд"
        rSide={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Нэмэх
          </Button>
        }
      />
      {/* <PageHeader title="Зээл" sub="Хүү, шимтгэл, эргэн төлөлтийн нөхцөл" /> */}

      <div className="toolbar">
        <Input
          className="search"
          prefix={<IconSearch size={14} color="#bbb" />}
          placeholder="Зээлийн нэр хайх..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          allowClear
        />

        <Select
          className="status"
          placeholder="Зээлийн төрөл"
          allowClear
          value={type}
          onChange={onTypeChange}
          options={LOAN_TYPE_OPTIONS}
        />

        <Select
          className="status"
          placeholder="Ангилал"
          allowClear
          value={categoryId}
          onChange={onCategoryChange}
          options={LOAN_CATEGORY_OPTIONS}
        />
      </div>

      <div className="table-wrap">
        <Table
          rowKey="id"
          loading={{ spinning: loading, size: "small" }}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: meta?.page,
            pageSize: meta?.limit,
            total: meta?.total,
            onChange: (p) => setPage(p),
          }}
        />
      </div>

      <LoanFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        editingRow={editingRow}
        onSuccess={onDrawerSuccess}
      />

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        loading={deleting}
        title="Итгэлтэй байна уу?"
        description={
          <>
            <span style={{ fontWeight: 600 }}>"Зээл устгах"</span> үйлдлийг
            буцаах боломжгүйг анхаарна уу.
          </>
        }
        variant="error"
      />
    </section>
  );
}
